import type { RouteRecordRaw } from 'vue-router'
import { P } from '@shared/domain/permissions'
import { placeholder } from '../placeholder'

/** Public landing for the anchor's invitation link (Stage 3). */
const invitation: RouteRecordRaw = {
  path: '/invite/:token',
  component: () => import('@/layouts/AuthLayout.vue'),
  children: [
    {
      path: '',
      name: 'supplier.invitation',
      component: placeholder,
      meta: { titleKey: 'menu.supplier.invitation', stage: '3' },
    },
  ],
}

/** Stage 3 registration: shown in the workspace shell, reachable before approval. */
const registration: RouteRecordRaw = {
  path: 'register',
  meta: {
    allowBeforeActivation: true,
    menu: { labelKey: 'menu.supplier.registration', icon: 'ClipboardList', order: 2 },
  },
  children: [
    {
      path: '',
      name: 'supplier.register',
      redirect: { name: 'supplier.register.step', params: { step: 'profile' } },
      meta: {
        permission: P.SUPPLIER_REGISTRATION_EDIT,
        menu: { labelKey: 'menu.supplier.registrationWizard' },
      },
    },
    {
      path: 'status',
      name: 'supplier.register.status',
      component: placeholder,
      meta: { menu: { labelKey: 'menu.supplier.statusTracker' }, stage: '3' },
    },
    {
      path: ':step',
      name: 'supplier.register.step',
      component: placeholder,
      meta: {
        permission: P.SUPPLIER_REGISTRATION_EDIT,
        titleKey: 'menu.supplier.registrationWizard',
        stage: '3',
      },
    },
  ],
}

const workspace: RouteRecordRaw = {
  path: '/supplier',
  component: () => import('@/layouts/WorkspaceLayout.vue'),
  meta: { workspace: 'supplier', requiresAuth: true },
  children: [
    { path: '', redirect: { name: 'supplier.dashboard' } },
    registration,
    {
      path: 'dashboard',
      name: 'supplier.dashboard',
      component: () => import('@ws/supplier/dashboard/views/SupplierDashboard.vue'),
      meta: { menu: { labelKey: 'menu.dashboard', icon: 'LayoutDashboard', order: 1 } },
    },
    {
      path: 'invoices',
      meta: { menu: { labelKey: 'menu.invoices', icon: 'FileText', order: 3 } },
      children: [
        {
          path: 'available',
          name: 'supplier.invoices.available',
          component: () => import('@ws/supplier/invoices/views/AvailableInvoices.vue'),
          meta: { menu: { labelKey: 'menu.supplier.availableInvoices' } },
        },
        {
          path: '',
          name: 'supplier.invoices',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.supplier.allInvoices' }, stage: '4' },
        },
        {
          path: 'ineligible',
          name: 'supplier.invoices.ineligible',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ineligible' }, stage: '4' },
        },
      ],
    },
    {
      path: 'financing',
      meta: { menu: { labelKey: 'menu.financing', icon: 'Banknote', order: 4 } },
      children: [
        { path: '', redirect: { name: 'supplier.financing.list' } },
        {
          path: 'new/:step?',
          name: 'supplier.financing.new',
          component: () => import('@ws/supplier/financing/views/NewFinancingRequest.vue'),
          meta: {
            permission: P.SUPPLIER_FINANCING_SUBMIT,
            menu: { labelKey: 'menu.financingNew' },
          },
        },
        {
          path: 'requests',
          name: 'supplier.financing.list',
          component: () => import('@ws/supplier/financing/views/FinancingRequestList.vue'),
          meta: { menu: { labelKey: 'menu.financingRequests' } },
        },
        {
          path: 'requests/:id',
          name: 'supplier.financing.detail',
          props: true,
          component: () => import('@ws/supplier/financing/views/FinancingRequestDetail.vue'),
          meta: { titleKey: 'financing.detail.title' },
        },
        {
          path: 'disbursements',
          name: 'supplier.financing.disbursements',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.supplier.disbursements' }, stage: '7' },
        },
      ],
    },
    {
      path: 'repayments',
      name: 'supplier.repayments',
      component: placeholder,
      meta: {
        menu: { labelKey: 'menu.supplier.repayments', icon: 'Repeat', order: 5 },
        stage: '8–9',
      },
    },
    {
      path: 'account',
      meta: { menu: { labelKey: 'menu.supplier.account', icon: 'Building2', order: 6 } },
      children: [
        { path: '', redirect: { name: 'supplier.account.profile' } },
        {
          path: 'profile',
          name: 'supplier.account.profile',
          component: placeholder,
          meta: {
            permission: P.SUPPLIER_ACCOUNT_MANAGE,
            menu: { labelKey: 'menu.supplier.profileAccount' },
            stage: '3',
          },
        },
        {
          path: 'users',
          name: 'supplier.account.users',
          component: placeholder,
          meta: {
            permission: P.SUPPLIER_ACCOUNT_MANAGE,
            menu: { labelKey: 'menu.users' },
            stage: '3',
          },
        },
        {
          path: 'documents',
          name: 'supplier.account.documents',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.documents' }, stage: '3' },
        },
      ],
    },
  ],
}

export const supplierRoutes: RouteRecordRaw[] = [invitation, workspace]
